import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Calculator, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SectionHead, money } from "./acqUi";

const NUM = [
  ["adjusted_premium", "Adjusted Premium of the acquisition ($)", "Rolling 12-month gross folio premium; reduced-commission series count at 50%."],
  ["selling_avg_monthly_pl_nb", "Selling agency avg. monthly Personal Lines net NB premium ($)", "12-month evaluation average."],
  ["quarterly_nb_commission", "Your expected quarterly net NB commission — Life & P&C excl. Bristol West ($)", null],
  ["quarterly_bw_commission", "Expected quarterly Bristol West net NB commission, excl. CA ($)", null],
  ["quarterly_life_ip", "Expected Life policies issued & paid per quarter", null],
  ["reserve_pc_commission", "Fast Start: qualifying Life & P&C NB commissions before the Acquisition ($)", null],
  ["reserve_bw_commission", "Fast Start: qualifying Bristol West NB commissions before the Acquisition ($)", null],
];

function Field({ k, label, hint, v, set }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input type="number" min="0" inputMode="decimal" value={v[k] ?? ""} onChange={(e) => set({ ...v, [k]: e.target.value })} className="mt-1" data-testid={`calc-${k}`} />
      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function Picker({ label, value, onChange, items, testId }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">{items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function Result({ r }) {
  if (!r.eligible) return <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="calc-not-eligible">{r.message}</div>;
  return (
    <div className="space-y-4" data-testid="calc-result">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Bonus schedule", r.schedule], ["Premium band", r.band], ["Est. Monthly Bonus Program", money(r.monthly_bonus_total)], ["Est. Fast Start", money(r.fast_start)]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-slate-50 p-3"><p className="text-[11px] uppercase tracking-wide text-slate-500">{l}</p><p className="font-semibold text-slate-900 text-sm mt-0.5" data-testid={`calc-out-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</p></div>
        ))}
      </div>
      <div className="overflow-x-auto tr-scroll rounded-lg border border-slate-200">
        <table className="w-full text-sm" data-testid="calc-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500"><tr>{["Quarter", "Tier applied", "Life & P&C rate", "BW rate", "Est. bonus", "Leads Bucks", "Tier achieved"].map((h) => <th key={h} className="text-left px-3 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>{r.rows.map((x) => (
            <tr key={x.quarter} className="border-t border-slate-100">
              <td className="px-3 py-1.5">Q{x.quarter}</td><td className="px-3 py-1.5">{x.tier_applied}</td><td className="px-3 py-1.5">{x.pc_rate}%</td><td className="px-3 py-1.5">{x.bw_rate}%</td>
              <td className="px-3 py-1.5 font-medium">{money(x.bonus)}</td><td className="px-3 py-1.5">{x.leads_bucks == null ? "—" : money(x.leads_bucks)}</td><td className="px-3 py-1.5">{x.tier_achieved}</td>
            </tr>))}</tbody>
        </table>
      </div>
      <ul className="text-[11px] text-slate-500 space-y-0.5">{r.notes.map((n) => <li key={n}>• {n}</li>)}</ul>
    </div>
  );
}

export default function BonusCalculator({ text }) {
  const [opts, setOpts] = useState(null);
  const [v, setV] = useState({ qualification_method: "standard", area: "1", quarterly_life_ip: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/acquisition/calculator/options").then((r) => setOpts(r.data)).catch(() => setOpts({ areas: [], methods: [] })); }, []);
  const run = async () => {
    setBusy(true);
    try { setRes((await api.post("/acquisition/calculator/estimate", Object.fromEntries(Object.entries(v).map(([k, x]) => [k, ["area", "qualification_method"].includes(k) ? x : Number(x || 0)])))).data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <section id="acq-calculator" className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-8" data-testid="acq-calculator-section">
      <SectionHead eyebrow="Bonus Calculator" title="Estimate Your Bonus Opportunity" text={text} right={<Calculator className="w-8 h-8 text-[#006090]" />} />
      {!opts ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
        <div className="grid lg:grid-cols-[360px_1fr] gap-6">
          <div className="space-y-3">
            <Picker label="Buyer qualification method" value={v.qualification_method} onChange={(x) => setV({ ...v, qualification_method: x })} items={opts.methods} testId="calc-method" />
            <Picker label="Area (from your approved office ZIP Code)" value={v.area} onChange={(x) => setV({ ...v, area: x })} items={opts.areas} testId="calc-area" />
            {NUM.map(([k, l, h]) => <Field key={k} k={k} label={l} hint={h} v={v} set={setV} />)}
            <Button onClick={run} disabled={busy} className="w-full bg-[#0B2E4F] hover:bg-[#123d66] text-white" data-testid="calc-run">{busy ? "Calculating…" : "Estimate"}</Button>
            <p className="text-[11px] text-slate-500">Not sure of your Area? Your district team can confirm it from your office ZIP Code.</p>
          </div>
          <div>{res ? <Result r={res} /> : <div className="h-full min-h-[200px] rounded-xl border border-dashed border-slate-300 flex items-center justify-center text-sm text-slate-500 p-6 text-center" data-testid="calc-empty">Enter your inputs and select Estimate.</div>}</div>
        </div>
      )}
      <p className="mt-5 text-xs text-slate-500 flex items-start gap-1.5" data-testid="calc-disclaimer"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />Estimates are informational. Official Farmers records and current program terms determine actual eligibility and results.</p>
    </section>
  );
}
